import PropTypes from "prop-types";
import React, { useState, useRef, useEffect } from "react";
import {
  DatePicker,
  TimePicker,
  TextField,
  Checkbox,
  SelectField,
} from "react-md";
import MuiTextField from "components/Shared/MuiTextField";
import {
  DATA_TYPE_NUMBER,
  DATA_TYPE_MULTI_LINE_STRING,
  DATA_TYPE_NUMBER_UNFORMATTED,
  DATA_TYPE_PHONE_NUMBER,
  DATA_TYPE_MONEY,
  DATA_TYPE_BOOLEAN,
  DATA_TYPE_DATE,
  DATA_TYPE_DATE_TIME,
  DATA_TYPE_TIME,
  DATA_TYPE_DAYS_ELAPSED,
  DATA_TYPE_DROPDOWN,
  DATA_TYPE_DROPDOWN2,
  DATA_TYPE_PASSWORD,
  DATA_TYPE_ADDRESS_AUTOCOMPLETE,
  DATA_TYPE_CREDIT_CARD,
  DATA_TYPE_NUMBER_STRING,
  DATA_TYPE_PERCENT_WHOLE_NUMBER,
  DATA_TYPE_NUMBER_NEGATIVE_OR_POSITIVE,
} from "typeConstants/dataTypes";
import { MSelect } from "components/Shared";
import {
  dateTimeToStringWithNumOfDays,
  dateToStringYy,
  dateTimeToString,
} from "components/Shared/Formatters";
import {
  normalizeDecimalMoney,
  normalizeNumberVariableDecimal,
  normalizePhone,
  SearchGooglePlaces,
  normalizeCreditCardNumber,
  normalizeInteger,
  normalizeIntegerNegativeOrPositive,
} from "components/FormElements";
import moment from "moment-timezone";

export default function MField({
  id,
  className,
  dataType,
  dataTypeOptions,
  value,
  onChange,
  ariaLabel,
  onBlur,
  getControlMethods,
  placeholder,
  hideDatePickerLabel,
}) {
  const [controlValue, setControlValue] = useState(value);
  const [initialValue, setInitialValue] = useState(null);
  const previousValue = useRef(null);

  useEffect(() => {
    if (getControlMethods) {
      getControlMethods(id, {
        getValue: () => controlValue,
        setValue: (v) => { setControlValue(v); onChange(id, v); },
        setInitialObjectValue: (v) => setInitialValue(v),
      });
    }
  }, []);

  const getControlLabel = (controlValue) => {
    if (initialValue && !controlValue) {
      let option = dataTypeOptions.options.find(s =>
        s.value && s.value === initialValue || s === initialValue);
      if (option) {
        if (!option.label) option = { label: option, value: option };
        setInitialValue(null);
        setControlValue(option); onChange(id, option);
        return option.label;
      }
    }
    if (Array.isArray(controlValue)) return controlValue;
    return controlValue?.label
      ? controlValue.label
      : controlValue?.value
        ? controlValue.value
        : value;
  };

  let normalizer;
  switch (dataType) {
    case DATA_TYPE_MONEY:
      normalizer = normalizeDecimalMoney;
      break;
    case DATA_TYPE_NUMBER:
      normalizer = normalizeNumberVariableDecimal(dataTypeOptions?.numDigitsAfterDecimal);
      break;
    case DATA_TYPE_PHONE_NUMBER:
      normalizer = normalizePhone;
      break;
    case DATA_TYPE_NUMBER_UNFORMATTED:
    case DATA_TYPE_NUMBER_STRING:
      normalizer = normalizeNumberVariableDecimal(0, true);
      break;
    case DATA_TYPE_CREDIT_CARD:
      normalizer = normalizeCreditCardNumber;
      break;
    case DATA_TYPE_PERCENT_WHOLE_NUMBER:
      normalizer = normalizeInteger;
      break;
    case DATA_TYPE_NUMBER_NEGATIVE_OR_POSITIVE:
      normalizer = normalizeIntegerNegativeOrPositive;
  }

  const wrappedOnChange = (...params) => {
    previousValue.current = controlValue;
    let newValue;
    if (normalizer) params[0] = normalizer(params[0], previousValue.current);
    // Note on DATES: 
    // we are setting the controlValue as a javascript date object and using formatting for the display value.
    // we are tracking the value to save as a string so that serializers/deserializers do not mess up the time zone info.
    switch (dataType) {
      case DATA_TYPE_NUMBER:
      case DATA_TYPE_MONEY:
      case DATA_TYPE_NUMBER_UNFORMATTED:
        newValue = parseFloat(params[0]?.replace(/[^\d.]/g, ""));
        setControlValue(params[0]);
        break;
      case DATA_TYPE_PHONE_NUMBER:
        newValue = params[0]?.replace(/[^\d]/g, "");
        setControlValue(params[0]);
        break;
      case DATA_TYPE_DROPDOWN2:
        if (Array.isArray(params[0])) {
          newValue = params[0].map(o => o.value);
        } else {
          newValue = params[0];
        }
        setControlValue(params[0]);
        break;      
      case DATA_TYPE_DATE:
      case DATA_TYPE_DAYS_ELAPSED:
        newValue = params[1];
        if (!newValue) {
          // This allows the date time to be cleared by the delete button, otherwise it is undefined and will not serialize
          newValue = null;
          setControlValue(null);
          break;
        }
        setControlValue(newValue);
        newValue = moment.parseZone(newValue).format();
        break;
      case DATA_TYPE_DATE_TIME:
        {
          newValue = params[1];
          if (!newValue) {
            // This allows the date time to be cleared by the delete button, otherwise it is undefined and will not serialize
            newValue = null;
            setControlValue(null);
            break;
          }
          let previousDate = new Date(previousValue.current);
          if (!previousValue.current) {
            // Clear out the hours otherwise the control will set it to the current time
            newValue.setHours(0, 0, 0, 0);
          } else if (newValue.getDate() !== previousDate.getDate()) {
            // The date was changed after the time was set, keep the users time selection when changing dates
            const hours = previousDate.getHours();
            const mins = previousDate.getMinutes();
            newValue.setHours(hours, mins);
          }
          setControlValue(newValue);
          newValue = moment.parseZone(newValue).format();
        }
        break;
      case DATA_TYPE_TIME:
        newValue = params[0];
        setControlValue(params[0]);
        break;
      case DATA_TYPE_ADDRESS_AUTOCOMPLETE:
        newValue = params[0];
        setControlValue(dataTypeOptions.streetAddressOnly ? params[0].streetAddress : params[0].formatted);
        break;
      case DATA_TYPE_CREDIT_CARD:
      case DATA_TYPE_NUMBER_STRING:
      case DATA_TYPE_PERCENT_WHOLE_NUMBER:
        newValue = params[0]?.replace(/[^\d]/g, "");
        setControlValue(params[0]);
        break;
      case DATA_TYPE_NUMBER_NEGATIVE_OR_POSITIVE:
        newValue = params[0]?.replace(/(^-|-\d)|[^0-9]/g, '$1');
        setControlValue(params[0]);
        break;
      default:
        newValue = params[0];
        setControlValue(params[0]);
        break;
    }
    onChange(id, newValue);
  };

  let displayValue;
  switch (dataType) {
    case DATA_TYPE_DROPDOWN2:
      displayValue = getControlLabel(controlValue);
      break;
    case DATA_TYPE_DATE_TIME:
      displayValue = dateTimeToString(controlValue);
      break;
    case DATA_TYPE_DATE:
      displayValue = dateToStringYy(controlValue);
      break;
    case DATA_TYPE_DAYS_ELAPSED:
      displayValue = dateTimeToStringWithNumOfDays(controlValue);
      break;
    case DATA_TYPE_ADDRESS_AUTOCOMPLETE:
      // We don't want to apply the default to this type otherwise it can display [object Object] in the field
      break;
    default:
      // Cast value to string as 0's are falsey and mess up other logic whereas "0" is not falsey
      displayValue = controlValue === null || controlValue === undefined ? "" : `${controlValue}`;
      break;
  }
  if (normalizer) displayValue = normalizer(displayValue, previousValue.current);

  switch (dataType) {
    case DATA_TYPE_DATE:
    case DATA_TYPE_DAYS_ELAPSED:
    case DATA_TYPE_DATE_TIME:
      return (
        <div className="mcard-date-picker">
          <DatePicker
            id={id}
            className={className}
            autoOk
            onChange={wrappedOnChange}
            defaultValue={controlValue}
            portal
            lastChild
            renderNode={null}
            disableScrollLocking
            minDate={dataTypeOptions?.minDate}
            maxDate={dataTypeOptions?.maxDate}
          />
          {// Check for value as we dont want to show the time picker unless a date has already been picked
            dataType === DATA_TYPE_DATE_TIME && controlValue && (
              <TimePicker
                id={id}
                autoOk
                onChange={wrappedOnChange}
                value={controlValue}
                portal
                lastChild
                renderNode={null}
              />
            )}
          {!(dataTypeOptions?.nullable === false) && (
            <i
              className="md-icon material-icons"
              title="Clear Value"
              onClick={wrappedOnChange}
            >
              delete_forever
            </i>
          )}
          {!hideDatePickerLabel && <div className="date-picker-label">
            {displayValue}
          </div>}
        </div>
      );
    case DATA_TYPE_TIME:
      return (
        <div className="mcard-time-input">
          <MuiTextField
            id={id}
            className={className}
            type="time"
            onChange={(event) => wrappedOnChange(event.target.value)}
            onBlur={onBlur}
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={controlValue ? controlValue : ""}
          />
        </div>
      );
    case DATA_TYPE_BOOLEAN:
      return (
        <Checkbox
          id={id}
          className={className}
          name={id}
          aria-label={ariaLabel}
          onChange={wrappedOnChange}
          checked={value}
        />
      );
    case DATA_TYPE_MULTI_LINE_STRING:
      return (
        <TextField
          id={id}
          className={className}
          onChange={wrappedOnChange}
          onBlur={onBlur}
          value={displayValue || ""}
          rows={1} // Specifying rows makes it a multiline edit control
          placeholder={placeholder}
        />
      );
    case DATA_TYPE_DROPDOWN:
      return (
        <div className={`mcard-select-field ${className || ""}`}>
          {!(dataTypeOptions?.nullable === false) && (
            <i
              className="md-icon material-icons"
              title="Clear Value"
              onClick={() => wrappedOnChange()}
            >
              delete_forever
            </i>
          )}
          <SelectField
            id={id}
            aria-label={ariaLabel}
            menuItems={dataTypeOptions.options}
            simplifiedMenu={false}
            value={displayValue || ""} // If the null out button is pressed the dropdown will think it is no loner a controlled element because we are passing it a null value
            onChange={wrappedOnChange}
            anchor={{
              x: SelectField.HorizontalAnchors.INNER_LEFT,
              y: SelectField.VerticalAnchors.BOTTOM
            }}
          />
        </div>);
    case DATA_TYPE_DROPDOWN2:
      return (
        <MSelect
          id={id}
          className={className}
          sortFunc={dataTypeOptions.sortFunc}
          value={displayValue}
          onChange={wrappedOnChange}
          options={dataTypeOptions.options}
          isClearable={!(dataTypeOptions?.nullable === false)}
          isMulti={dataTypeOptions.isMultiSelect}
          shortMulti={dataTypeOptions.isShortMulti}
          creatable={dataTypeOptions.creatable}
          standardOptionComponents
          placeholder={placeholder}
        />);
    case DATA_TYPE_PASSWORD:
      return (
        <TextField
          id={id}
          className={className}
          onChange={wrappedOnChange}
          onBlur={onBlur}
          value={displayValue || ""}
          type="password"
        />
      );
    case DATA_TYPE_ADDRESS_AUTOCOMPLETE:
      return (
        <SearchGooglePlaces
          id={id}
          onChange={wrappedOnChange}
          value={displayValue || ""}
          streetAddressOnly={dataTypeOptions.streetAddressOnly}
        />
      );
    default:
      return (
        <TextField
          id={id}
          className={className}
          onChange={wrappedOnChange}
          onBlur={onBlur}
          value={displayValue || ""}
          placeholder={placeholder}
        />
      );
  }
}

MField.propTypes = {
  id: PropTypes.string, // Set this to silence some warnings
  className: PropTypes.string,  // Class name provided to control
  dataType: PropTypes.string, // See typeConstants/dataTypes
  dataTypeOptions: PropTypes.object, // See dataTypeOptions in MCard proptypes
  value: PropTypes.oneOfType([  // An initial value to display in the control
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.object,
    PropTypes.array,
  ]),
  onChange: PropTypes.func, // Function that will be called with the id (key) and changed value of the control
  ariaLabel: PropTypes.string,  // Provide this to silence some warnings
  onBlur: PropTypes.func, // Not supported for all control types, see above switch statement
  /* 
    Callback function that will be called with the id (key) and an object that has functions for interacting with the field:
    getValue: returns the value of the control. This will be the raw value, not the formatted value and could be an object if coming from a dropdown
    setValue: sets the value of the control and calls the onchange function
    setInitialObjectValue: sets the initial value of an object valued control, and calls the onChange func. 
  */
  getControlMethods: PropTypes.func,
  placeholder: PropTypes.string,
  // hide the label under the date picker that just... shows the date again
  hideDatePickerLabel: PropTypes.bool,
};
